var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
/* eslint-disable class-methods-use-this */
import { Group, PointLight, DirectionalLight, Vector3 } from "three";
import { disposeGeometryRecursively } from "@rendering/vortex-core";
import { RectangularGeometry, CustomPathGeometry } from "../flatGeometries";
export var FlatSheetOrientation;
(function (FlatSheetOrientation) {
  FlatSheetOrientation[FlatSheetOrientation["Horizontal"] = 0] = "Horizontal";
  FlatSheetOrientation[FlatSheetOrientation["Vertical"] = 1] = "Vertical";
})(FlatSheetOrientation || (FlatSheetOrientation = {}));
export class FlatSheetProduct {
  constructor(config) {
    var _a, _b;
    Object.defineProperty(this, "type", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: "flat sheet"
    });
    Object.defineProperty(this, "config", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "flatGeometry", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "initializedGeometry", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "surfaceMapping", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    // Separate content and body layers by 100 micrometers
    Object.defineProperty(this, "LayerOffset", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 0.1
    });
    // Clone
    this.config = JSON.parse(JSON.stringify(config));
    if (this.config.customPath) {
      this.flatGeometry = new CustomPathGeometry(this.config.customPath, this.config.trim);
    } else {
      this.flatGeometry = new RectangularGeometry();
    }
    if (this.config.substrates.length !== 3) {
      throw new Error("The FlatSheetProductConfig must contain three substrates for the front, back, and body!");
    }
    // Build the surface mapping
    this.surfaceMapping = {
      front: {
        substrate: this.config.substrates[0],
        finishes: (_a = this.config.finishes) === null || _a === void 0 ? void 0 : _a.front,
        fullBleed: this.config.fullBleed,
        trim: this.config.trim,
        page: this.config.pageMapping ? this.config.pageMapping.front : 1,
        focalPoint: {
          cameraView: new Vector3(0, 0, -1)
        }
      },
      body: {
        substrate: this.config.substrates[1],
        fullBleed: this.config.fullBleed
      },
      back: {
        substrate: this.config.substrates[2],
        finishes: (_b = this.config.finishes) === null || _b === void 0 ? void 0 : _b.back,
        fullBleed: this.config.fullBleed,
        trim: this.config.trim,
        page: this.config.pageMapping ? this.config.pageMapping.back : 2,
        focalPoint: {
          cameraView: new Vector3(0, 0, 1)
        }
      }
    };
  }
  getGeometry() {
    return __awaiter(this, void 0, void 0, function* () {
      var _a, _b;
      // If existing geometry already exists dispose of it
      if (this.initializedGeometry) {
        disposeGeometryRecursively(this.initializedGeometry);
      }
      const physicalSize = this.getPhysicalSize();
      this.initializedGeometry = new Group();
      const front = this.flatGeometry.buildFront(physicalSize);
      front.uuid = "front";
      front.position.z = this.config.depth * 0.5 + this.LayerOffset;
      this.initializedGeometry.add(front);
      // Add premium finishes if they are configured
      if ((_a = this.config.finishes) === null || _a === void 0 ? void 0 : _a.front) {
        let finishDepthOffset = this.LayerOffset * 2;
        this.config.finishes.front.forEach(finish => {
          var _a;
          const frontFinish = this.flatGeometry.buildFront(physicalSize);
          frontFinish.uuid = "front-".concat(finish.name);
          frontFinish.position.z = this.config.depth * 0.5 + finishDepthOffset;
          finishDepthOffset += this.LayerOffset;
          (_a = this.initializedGeometry) === null || _a === void 0 ? void 0 : _a.add(frontFinish);
        });
      }
      const body = this.flatGeometry.buildBody(physicalSize);
      body.uuid = "body";
      this.initializedGeometry.add(body);
      const back = this.flatGeometry.buildBack(physicalSize);
      back.uuid = "back";
      back.position.z = this.config.depth * -0.5 - this.LayerOffset;
      back.rotation.y = Math.PI;
      this.initializedGeometry.add(back);
      if ((_b = this.config.finishes) === null || _b === void 0 ? void 0 : _b.back) {
        let finishDepthOffset = this.LayerOffset * 2;
        this.config.finishes.back.forEach(finish => {
          const backFinish = this.flatGeometry.buildBack(physicalSize);
          backFinish.uuid = "back-".concat(finish.name);
          // Set to 0 because we add it to the back mesh
          backFinish.position.x = 0;
          backFinish.position.y = 0;
          backFinish.position.z = finishDepthOffset;
          finishDepthOffset += this.LayerOffset;
          back.add(backFinish);
        });
      }
      if (this.config.orientation === FlatSheetOrientation.Vertical) {
        this.initializedGeometry.rotateZ(Math.PI / 2);
      }
      this.resolveTransparency();
      return this.initializedGeometry;
    });
  }
  getSurfaceMapping() {
    return this.surfaceMapping;
  }
  setProductSurface(surfaceId, productSurface) {
    if (this.surfaceMapping[surfaceId]) {
      this.surfaceMapping[surfaceId] = productSurface;
      this.resolveTransparency();
    } else {
      throw new Error("'".concat(surfaceId, "' is not a surface on this flat sheet product"));
    }
  }
  getStates() {
    return [{
      id: "default"
    }];
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setStates(_productState) {}
  getCurrentState() {
    return {
      id: "default"
    };
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setCurrentState(_stateId) {}
  getAnimationIds() {
    return [];
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  buildAnimation(_animationOptions) {
    throw new Error("Method not implemented for Flat Sheet products.");
  }
  getAreaLights(pointLightIntensity, directionalLightIntensity) {
    const lights = [];
    // Add in directional lighting
    const frontDirection = new DirectionalLight(0xffffff, 0.12 * directionalLightIntensity);
    frontDirection.position.set(0, 0, 200);
    const backDirectional = new DirectionalLight(0xffffff, 0.12 * directionalLightIntensity);
    backDirectional.position.set(0, 0, -200);
    lights.push(frontDirection);
    lights.push(backDirectional);
    // Add in point lighting
    const frontLight = new PointLight(0xffffff, 0.2625 * pointLightIntensity, 500);
    frontLight.position.set(0, 0, 300);
    const backLight = new PointLight(0xffffff, 0.2625 * pointLightIntensity, 500);
    backLight.position.set(0, 0, -300);
    lights.push(frontLight);
    lights.push(backLight);
    return lights;
  }
  preLoad() {
    return Promise.resolve();
  }
  dispose() {
    // Nothing to do
    if (!this.initializedGeometry) return;
    disposeGeometryRecursively(this.initializedGeometry);
    this.initializedGeometry = undefined;
  }
  // Creates the physical size from the provided trim if it exists, otherwise defaults to the fullbleed size
  getPhysicalSize() {
    if (this.config.trim) {
      return {
        width: this.config.trim.bottomRightX - this.config.trim.topLeftX,
        height: this.config.trim.bottomRightY - this.config.trim.topLeftY,
        depth: this.config.depth
      };
    }
    return {
      width: this.config.fullBleed.width,
      height: this.config.fullBleed.height,
      depth: this.config.depth
    };
  }
  // Ensures z-fighting will not cause artifacts for transparent materials
  resolveTransparency() {
    var _a;
    // Ensure geometry is loaded
    if (!this.initializedGeometry || !this.surfaceMapping) return;
    // Find the front substrate
    if (this.surfaceMapping.front) {
      const {
        substrate
      } = this.surfaceMapping.front;
      // Get the back mesh
      const backMesh = this.initializedGeometry.children.find(element => element.uuid === "back");
      // Toggle the back back mesh visibility based on transparency settings
      if (backMesh) {
        backMesh.visible = (_a = !substrate.supportsTransparency) !== null && _a !== void 0 ? _a : true;
      }
    }
  }
}